import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Slider } from '../components/ui/slider';
import { 
  ChevronLeft, Crown, Check, Zap, Star, 
  CreditCard, AlertTriangle, CheckCircle, Calculator, Calendar
} from 'lucide-react';
import axios from 'axios';
import { FEATURES } from '../config/features';
import { PaytmCheckoutButton } from '../components/PaytmCheckoutButton';
import { InstamojoCheckoutButton } from '../components/InstamojoCheckoutButton';
import { PaymentMethodModal } from '../components/PaymentMethodModal';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";

const PLANS = [
  {
    id: 'free',
    name: 'Free Trial',
    price_inr: 0,
    price_usd: 0,
    period: '15 days',
    description: 'Try the platform for free',
    features: [
      'Report missing persons',
      'Search all cases',
      'Submit sightings',
      'Basic case visibility'
    ],
    popular: false,
    visible: true,
  },
  {
    id: 'standard',
    name: 'Standard',
    price_inr: 20,
    price_usd: 0.24,
    period: 'per day',
    description: 'For families seeking help',
    features: [
      'Everything in Free',
      'Extended visibility past the 15-day free window',
      'Invoice download for every payment',
      'In-app & browser push notifications'
    ],
    popular: true,
    visible: true,
  },
  {
    // Hidden — will be activated later once Premium-only features
    // (boosted ranking, AI facial scanning, analytics) are built.
    // Flip `visible: true` to re-enable in the UI.
    id: 'premium',
    name: 'Premium',
    price_inr: 50,
    price_usd: 0.60,
    period: 'per day',
    description: 'Maximum visibility & features',
    features: [
      'Everything in Standard',
      'Geographic amplification',
      'AI facial scanning priority',
      'Advanced case analytics',
      'Priority support'
    ],
    popular: false,
    visible: false,
  }
];

const VISIBLE_PLANS = PLANS.filter(p => p.visible); // eslint-disable-line no-unused-vars

export default function SubscriptionPage() {
  const { user, token, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Stripe is the only active gateway (Razorpay was removed); Paytm is "coming soon".
  const [selectedPlan, setSelectedPlan] = useState('standard');
  const [selectedDays, setSelectedDays] = useState(30);
  const [customDaysInput, setCustomDaysInput] = useState('');

  // Guest-checkout fields (only used when no `user`)
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');

  const MIN_DAYS = 1;
  const MAX_DAYS = 365;

  const sanitizeDays = (raw) => {
    const n = parseInt(raw, 10);
    if (isNaN(n)) return MIN_DAYS;
    return Math.max(MIN_DAYS, Math.min(MAX_DAYS, n));
  };

  const handleSelectDays = (raw) => {
    setSelectedDays(sanitizeDays(raw));
  };
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  // Legacy paymentDetails state (was populated by the now-removed Razorpay
  // inline checkout). Kept as `null` so the success card renders without
  // the order summary block. Will be wired again when Paytm goes live.
  const paymentDetails = null;

  const sessionId = searchParams.get('session_id');
  const paymentSuccess = searchParams.get('payment_success');

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  useEffect(() => {
    if (paymentSuccess === 'true') {
      setShowPaymentSuccess(true);
      setSuccess('Payment successful! Your subscription is now active.');
      checkAuth();
      fetchSubscriptionStatus();
    }
  }, [paymentSuccess]);

  // Handle Paytm / Instamojo / SabPaisa redirect-back status params.
  // The webhook is the source of truth for activation; this hook only
  // surfaces the outcome to the UI and re-fetches subscription state.
  //
  // Special-case for SabPaisa: when their return URL comes back without
  // the merchant txn id (which happens on some UPI flows), our backend
  // redirects with sabpaisa=pending. But by the time the browser lands
  // here, the S2S webhook may have already flipped the subscription to
  // ACTIVE. Show 'success' instead of 'processing' in that case so we
  // don't scare the user with a red banner over an active subscription.
  const paytmParam = searchParams.get('paytm');
  const instamojoParam = searchParams.get('instamojo');
  const sabpaisaParam = searchParams.get('sabpaisa');
  useEffect(() => {
    const outcome = paytmParam || instamojoParam || sabpaisaParam;
    if (!outcome) return;

    let cancelled = false;

    // If the webhook already activated the subscription by the time
    // the browser landed here, surface success — regardless of the
    // outcome param we received. Poll a few times to give the S2S
    // event a chance to arrive (webhook race window is typically <5s).
    const pollForActivation = async (attempts = 0) => {
      if (cancelled || attempts >= 6) return false; // ~12s max
      try {
        const r = await axios.get(`${API}/payments/user-subscription`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) setSubscriptionStatus(r.data);
        const isActive =
          r?.data?.subscription_plan === 'standard' &&
          r?.data?.subscription_expires_at &&
          new Date(r.data.subscription_expires_at).getTime() > Date.now();
        if (isActive) return true;
      } catch (_) { /* ignore transient network errors */ }
      await new Promise((res) => setTimeout(res, 2000));
      return pollForActivation(attempts + 1);
    };

    (async () => {
      if (outcome === 'success') {
        setSuccess('Payment successful! Your subscription is now active.');
        setShowPaymentSuccess(true);
        checkAuth();
        fetchSubscriptionStatus();
        return;
      }
      if (outcome === 'failed') {
        // Even a "failed" callback can be a false-alarm if the webhook
        // beat it — verify subscription state before scaring the user.
        const active = await pollForActivation();
        if (cancelled) return;
        if (active) {
          setSuccess('Payment successful! Your subscription is now active.');
          setShowPaymentSuccess(true);
          checkAuth();
        } else {
          setError('Payment failed or was cancelled. No amount was deducted, please try again.');
        }
        return;
      }
      if (outcome === 'pending') {
        // Optimistic UI: show a subtle "confirming…" message and poll.
        // If the webhook already activated us, silently upgrade to success.
        setError('Confirming your payment with the bank — this usually takes a few seconds.');
        const active = await pollForActivation();
        if (cancelled) return;
        if (active) {
          setError(null);
          setSuccess('Payment successful! Your subscription is now active.');
          setShowPaymentSuccess(true);
          checkAuth();
        } else {
          setError('Payment is still being confirmed. Refresh this page in a minute — you don\'t need to pay again.');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [paytmParam, instamojoParam, sabpaisaParam]);

  useEffect(() => {
    if (selectedPlan && selectedPlan !== 'free') {
      calculatePrice();
    }
  }, [selectedPlan, selectedDays]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/user-subscription`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSubscriptionStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
    }
  };

  const calculatePrice = async () => {
    // Local synchronous calculation for instant feedback (no race condition).
    // The backend is still hit on actual checkout for authoritative pricing.
    const plan = PLANS.find((p) => p.id === selectedPlan);
    if (!plan) return;
    const days = Math.max(1, Math.min(365, parseInt(selectedDays, 10) || 0));
    setCalculatedPrice({
      plan: selectedPlan,
      plan_name: plan.name,
      days,
      price_per_day_inr: plan.price_inr,
      price_per_day_usd: plan.price_usd,
      total_inr: Math.round(plan.price_inr * days * 100) / 100,
      total_usd: Math.round(plan.price_usd * days * 100) / 100,
    });
  };

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setError('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sid}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.data.payment_status === 'paid') {
        setSuccess('Payment successful! Your subscription is now active.');
        await checkAuth();
        fetchSubscriptionStatus();
        // Guest-checkout users were created with a random password — send
        // them to the claim/set-password page so they can sign back in
        // later. checkAuth() above refreshes `user`, but it may not have
        // landed in state yet — read the freshly-fetched user from /me.
        try {
          const me = await axios.get(`${API}/auth/me`, {
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (me.data?.created_via_checkout) {
            navigate('/set-password?from=checkout', { replace: true });
          }
        } catch (e) {
          // /auth/me may 401 briefly right after Stripe redirect while the
          // guest-token cookie/localStorage sync races. Non-fatal — the
          // /set-password redirect will simply be skipped this time.
          console.warn('[subscription] post-checkout auth/me lookup failed:', e?.response?.status, e?.message);
        }
        return;
      } else if (response.data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        return;
      }

      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch (err) {
      console.error('Error checking payment status:', err);
    }
  };

  const handleSubscribe = async (planId) => {
    if (planId === 'free') return;

    // Guest checkout: collect email + name inline if not logged in.
    // The backend will create or attach a user, then return the Stripe URL.
    if (!user) {
      const email = (guestEmail || '').trim().toLowerCase();
      const name = (guestName || '').trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError('Please enter a valid email to continue.');
        return;
      }
      if (!name) {
        setError('Please enter your name to continue.');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        plan: planId,
        payment_gateway: 'stripe',
        days: selectedDays,
        origin_url: window.location.origin,
      };
      if (!user) {
        payload.guest_email = guestEmail.trim().toLowerCase();
        payload.guest_name = guestName.trim();
      }

      const response = await axios.post(`${API}/payments/create-checkout`, payload, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      // Guest checkout: the backend has set an httpOnly session cookie in
      // this same response, so the browser is already authenticated when
      // Stripe redirects back to /subscription?session_id=... — no need
      // to persist anything in localStorage.

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        setError('Could not initialise payment. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initiate payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-muted-foreground hover:text-primary">
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
                <span className="text-lg font-black text-slate-900">MISYOU</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        {/* Came from grace-window banner — contextual hint */}
        {searchParams.get('source') === 'grace_banner' && (
          <Alert className="mb-8 bg-red-50 border-red-300 max-w-xl mx-auto" data-testid="grace-source-hint">
            <Crown className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <strong>Subscribe once to unlock all your active cases.</strong>{' '}
              Standard plan starts at ₹20/day — pick the days that fit your timeline below.
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-black text-slate-900 mb-4">Choose Your Plan</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Upgrade your plan to increase visibility and maximize the chances of finding your loved one
          </p>
        </div>

        {/* Alerts */}
        {success && (
          <Alert className="mb-8 bg-green-50 border-green-200 max-w-xl mx-auto">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-700">{success}</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" className="mb-8 max-w-xl mx-auto">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Subscription Status */}
        {subscriptionStatus && (
          <Card className="mb-8 max-w-2xl mx-auto bg-white shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">Your Subscription Status</h3>
                  <div className="mt-2 space-y-1 text-sm">
                    {subscriptionStatus.trial_active && (
                      <p className="text-green-600 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Free trial active: {subscriptionStatus.trial_remaining_days} days remaining
                      </p>
                    )}
                    {subscriptionStatus.subscription_active && (
                      <p className="text-primary flex items-center gap-2">
                        <Crown className="w-4 h-4" />
                        <span>
                          <span className="capitalize">{subscriptionStatus.subscription_plan}</span> plan:{' '}
                          {subscriptionStatus.subscription_remaining_days} day{subscriptionStatus.subscription_remaining_days === 1 ? '' : 's'} remaining
                          {subscriptionStatus.subscription_expires_at && (
                            <span className="block text-xs text-slate-500 mt-0.5">
                              Expires on{' '}
                              {new Date(subscriptionStatus.subscription_expires_at).toLocaleString('en-IN', {
                                weekday: 'short', day: '2-digit', month: 'short',
                                hour: '2-digit', minute: '2-digit', hour12: true,
                                timeZone: 'Asia/Kolkata',
                              })}{' '}
                              IST
                            </span>
                          )}
                        </span>
                      </p>
                    )}
                    {!subscriptionStatus.can_post_free && (
                      <p className="text-amber-600 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Free trial ended. Subscribe to continue posting.
                      </p>
                    )}
                  </div>
                </div>
                <Badge className={subscriptionStatus.can_post_free ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                  {subscriptionStatus.can_post_free ? 'Active' : 'Expired'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Partners disclosure — kept as a compact single strip
            because the actual method picker (UPI / Card / Netbanking) now
            lives in the "Pay ₹X" full-screen modal further down. This strip
            is purely informational + legal transparency for reviewers. */}
        <div
          className="max-w-3xl mx-auto mb-8 rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3"
          data-testid="payment-partners-strip"
        >
          <p className="text-[11px] uppercase tracking-widest text-slate-500 text-center mb-2">
            Trusted Payment Partners
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs">
            {FEATURES.SABPAISA_ENABLED && (
              <div className="flex items-center gap-1.5" data-testid="partner-sabpaisa">
                <span className="font-semibold text-slate-700 tracking-tight">SabPaisa</span>
                <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[9px] uppercase tracking-wider">
                  Active
                </Badge>
              </div>
            )}
            {FEATURES.PAYTM_ENABLED && (
              <div className="flex items-center gap-1.5" data-testid="partner-paytm">
                <span className="font-semibold text-slate-700 tracking-tight">Paytm</span>
                <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[9px] uppercase tracking-wider">
                  Active
                </Badge>
              </div>
            )}
          </div>
          <p className="text-[11px] text-slate-500 text-center mt-2">
            You'll pick UPI, Card or Netbanking on the checkout screen.
          </p>
        </div>

        {/* Day Selector & Price Calculator */}
        <Card className="mb-8 max-w-2xl mx-auto bg-gradient-to-r from-primary/5 to-blue-50 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-primary" />
              Calculate Your Subscription
            </CardTitle>
            <CardDescription>
              Select the number of days you want to post your report (after 15-day free trial)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Plan Selection */}
            <div>
              <Label className="mb-3 block">Select Plan</Label>
              <div className="flex gap-4">
                <Button
                  variant={selectedPlan === 'standard' ? 'default' : 'outline'}
                  onClick={() => setSelectedPlan('standard')}
                  className="flex-1"
                  data-testid="select-plan-standard-btn"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Standard (₹20/day)
                </Button>
                {PLANS.find(p => p.id === 'premium')?.visible && (
                  <Button
                    variant={selectedPlan === 'premium' ? 'default' : 'outline'}
                    onClick={() => setSelectedPlan('premium')}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    data-testid="select-plan-premium-btn"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Premium (₹50/day)
                  </Button>
                )}
              </div>
            </div>

            {/* Days Selector */}
            <div>
              <Label className="mb-3 block">Number of Days: <span className="font-bold text-primary" data-testid="selected-days-label">{selectedDays}</span></Label>
              <Slider
                value={[selectedDays]}
                onValueChange={(value) => handleSelectDays(value[0])}
                min={MIN_DAYS}
                max={MAX_DAYS}
                step={1}
                className="mb-2"
                data-testid="days-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>1 day</span>
                <span>1 month</span>
                <span>3 months</span>
                <span>1 year</span>
              </div>
              
              {/* Quick Select */}
              <div className="flex flex-wrap gap-2 mt-4">
                {[7, 15, 30, 60, 90, 180, 365].map((days) => (
                  <Button
                    key={days}
                    variant={selectedDays === days ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleSelectDays(days)}
                    className="text-xs"
                    data-testid={`quick-days-${days}-btn`}
                  >
                    {days <= 30 ? `${days}d` : days < 365 ? `${Math.round(days/30)}m` : '1y'}
                  </Button>
                ))}
              </div>

              {/* Custom days input */}
              <div className="mt-5 p-4 rounded-lg bg-white border border-slate-200">
                <Label htmlFor="custom-days-input" className="text-sm font-semibold text-slate-700 mb-2 block">
                  Or enter any custom number of days
                </Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="custom-days-input"
                    type="number"
                    inputMode="numeric"
                    min={MIN_DAYS}
                    max={MAX_DAYS}
                    placeholder="e.g., 19"
                    value={customDaysInput}
                    onChange={(e) => setCustomDaysInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (customDaysInput.trim()) {
                          handleSelectDays(customDaysInput);
                          setCustomDaysInput('');
                        }
                      }
                    }}
                    className="flex-1"
                    data-testid="custom-days-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      if (customDaysInput.trim()) {
                        handleSelectDays(customDaysInput);
                        setCustomDaysInput('');
                      }
                    }}
                    disabled={!customDaysInput.trim()}
                    data-testid="apply-custom-days-btn"
                  >
                    Apply
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Type any number between {MIN_DAYS} and {MAX_DAYS}. The amount updates automatically.
                </p>
              </div>
            </div>

            {/* Price Display */}
            {calculatedPrice && (
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-muted-foreground">Plan</span>
                  <span className="font-semibold">{calculatedPrice.plan_name}</span>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-semibold">{calculatedPrice.days} days</span>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-muted-foreground">Price per day</span>
                  <span className="font-semibold">
                    ₹{calculatedPrice.price_per_day_inr}
                  </span>
                </div>
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-slate-900">Total Amount</span>
                    <span className="text-2xl font-black text-primary">
                      ₹{calculatedPrice.total_inr}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 text-right">
                    Inclusive of all taxes
                  </p>
                </div>

                {/* Guest-checkout fields — only when not signed in */}
                {!user && (
                  <div className="mt-4 p-4 bg-white border border-slate-200 rounded-lg space-y-3" data-testid="guest-checkout-block">
                    <p className="text-sm font-medium text-slate-900">
                      Guest checkout — no account required
                    </p>
                    <p className="text-xs text-muted-foreground">
                      We'll create your account automatically when payment succeeds. Already have one?{' '}
                      <a href="/login" className="text-primary underline">Sign in</a>.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="guest-name">Full name</Label>
                      <Input
                        id="guest-name"
                        type="text"
                        placeholder="As it appears on your card"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        data-testid="guest-name-input"
                        autoComplete="name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="guest-email">Email address</Label>
                      <Input
                        id="guest-email"
                        type="email"
                        placeholder="you@example.com"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        data-testid="guest-email-input"
                        autoComplete="email"
                      />
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      By continuing you agree to our{' '}
                      <a href="/terms" className="underline">Terms</a>,{' '}
                      <a href="/privacy" className="underline">Privacy Policy</a> and{' '}
                      <a href="/refund" className="underline">Refund Policy</a>.
                    </p>
                  </div>
                )}

                <Button
                  className="w-full mt-6 h-14 bg-primary hover:bg-primary/90 rounded-xl text-lg font-bold btn-press shadow-lg shadow-primary/20"
                  disabled={loading || (!FEATURES.STRIPE_ENABLED && !FEATURES.PAYTM_ENABLED && !FEATURES.INSTAMOJO_ENABLED)}
                  onClick={() => {
                    // Basic guest-field validation before opening the modal
                    if (!user) {
                      if (!guestEmail.trim() || !guestName.trim()) {
                        setError('Please enter your name and email to continue.');
                        return;
                      }
                    }
                    setError('');
                    setShowPaymentModal(true);
                  }}
                  data-testid="open-payment-modal-btn"
                >
                  {loading
                    ? 'Processing…'
                    : (!FEATURES.STRIPE_ENABLED && !FEATURES.PAYTM_ENABLED && !FEATURES.INSTAMOJO_ENABLED)
                      ? 'Payments temporarily unavailable'
                      : `Pay ₹${calculatedPrice?.total_inr ?? (selectedDays * 20)}`}
                </Button>
                <p className="text-[11px] text-center text-slate-500 mt-2">
                  Choose UPI, Card or Netbanking on the next screen
                </p>

                {/* Payment security disclosure — required by PayU / RBI
                    Payment Aggregator guidelines. Every merchant must
                    tell the customer HOW their payment data is protected. */}
                <div
                  className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3"
                  data-testid="payment-security-disclosure"
                >
                  <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold text-center">
                    Payment security
                  </p>
                  <ul className="text-[11px] text-slate-600 mt-2 space-y-1 leading-relaxed">
                    <li>&middot; Card / UPI credentials never touch MISYOU servers — handled by PCI-DSS-compliant gateway</li>
                    <li>&middot; No card storage on our side, no auto-renewal, no NACH mandate</li>
                    <li>&middot; INR only &middot; one-time subscription (customer picks 1–365 days upfront)</li>
                    <li>&middot; Refunds governed by our <Link to="/refund" className="underline hover:text-primary">Refund &amp; Cancellation Policy</Link></li>
                  </ul>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plans Comparison */}
        <h2 className="text-2xl font-bold text-center mb-6">Plan Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {PLANS.filter(p => p.visible).map((plan) => (
            <Card 
              key={plan.id}
              className={`relative shadow-xl ${
                plan.popular 
                  ? 'border-2 border-primary ring-4 ring-primary/10' 
                  : 'border-slate-200'
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-white px-4 py-1">
                    <Star className="w-3 h-3 mr-1" />
                    RECOMMENDED
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-4">
                <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-4 ${
                  plan.id === 'premium' ? 'bg-purple-100' : 
                  plan.id === 'standard' ? 'bg-primary/10' : 'bg-slate-100'
                }`}>
                  {plan.id === 'premium' ? (
                    <Crown className="w-6 h-6 text-purple-600" />
                  ) : plan.id === 'standard' ? (
                    <Zap className="w-6 h-6 text-primary" />
                  ) : (
                    <Star className="w-6 h-6 text-slate-600" />
                  )}
                </div>
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              
              <CardContent>
                <div className="text-center mb-6">
                  {plan.price_inr === 0 ? (
                    <div className="text-4xl font-black text-slate-900">Free</div>
                  ) : (
                    <>
                      <div className="text-4xl font-black text-slate-900">
                        ₹{plan.price_inr}
                      </div>
                      <div className="text-muted-foreground">{plan.period}</div>
                    </>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan.id !== 'free' && (
                  <Button
                    variant="outline"
                    className="w-full rounded-full"
                    onClick={() => {
                      setSelectedPlan(plan.id);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    Select {plan.name}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info Note */}
        <Card className="max-w-2xl mx-auto bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Calendar className="w-8 h-8 text-blue-600 shrink-0" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">How it works</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• You get 15 days free trial to post your missing person report</li>
                  <li>• After the trial, select the number of days you want your report to be visible</li>
                  <li>• The total amount is calculated based on your selected plan and duration</li>
                  <li>• Standard plan keeps your case visible past the free window and preserves your photos live for the paid duration</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <Card className="bg-white">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-2">How does the subscription work?</h3>
                <p className="text-muted-foreground text-sm">
                  Your subscription keeps your reported case visible past the free 15-day window
                  and generates an invoice. You choose how many days you want
                  the case to stay visible, and the total is calculated at ₹20/day (Standard plan).
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-2">What happens after my 15-day free trial?</h3>
                <p className="text-muted-foreground text-sm">
                  After your free trial ends, you'll need to subscribe to keep your case visible.
                  You can choose how many days you want to extend visibility.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-2">Is my payment secure?</h3>
                <p className="text-muted-foreground text-sm">
                  Yes — payments are processed by <strong>Paytm Payment Gateway</strong>, a PCI-DSS
                  compliant payment aggregator regulated by RBI. Your card and UPI details are
                  never stored on MISYOU&apos;s servers.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-2">Can I get a refund?</h3>
                <p className="text-muted-foreground text-sm">
                  Yes — first-time subscribers get a <strong>7-day money-back guarantee</strong>.
                  Read our full{' '}
                  <Link to="/refund" className="text-primary font-medium underline" data-testid="subscription-refund-link">
                    Refund &amp; Cancellation Policy
                  </Link>{' '}
                  for details on eligibility, response times, and how to request a refund.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Payment Success Modal */}
      {showPaymentSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in zoom-in-95 duration-300">
            {/* Success Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-8 text-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
              <p className="text-green-100">Your subscription is now active</p>
            </div>
            
            {/* Payment Details */}
            <div className="p-6">
              {paymentDetails && (
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-muted-foreground">Plan</span>
                    <span className="font-semibold capitalize">{paymentDetails.plan}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="font-semibold">{paymentDetails.days} days</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-muted-foreground">Amount Paid</span>
                    <span className="font-semibold text-green-600">₹{paymentDetails.amount}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-muted-foreground">Payment ID</span>
                    <span className="font-mono text-xs text-slate-600">{paymentDetails.paymentId?.slice(0, 20)}...</span>
                  </div>
                </div>
              )}
              
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Subscription Activated</p>
                    <p className="text-xs text-green-600 mt-1">
                      Your case stays visible for the full duration of your subscription and won&rsquo;t be auto-deleted at the end of the free window. An invoice is ready in My Subscriptions.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowPaymentSuccess(false);
                    navigate('/subscription');
                  }}
                >
                  View Subscription
                </Button>
                <Button
                  className="flex-1 bg-primary hover:bg-primary/90"
                  onClick={() => {
                    setShowPaymentSuccess(false);
                    navigate('/dashboard');
                  }}
                  data-testid="payment-success-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Razorpay-style method picker — one button opens this, user picks
          UPI / Card / Netbanking, we route to the best-fit gateway. Errors
          are surfaced inside the modal, so we DON'T close it on error —
          user can immediately try another method. */}
      <PaymentMethodModal
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        amount={calculatedPrice?.total_inr ?? (selectedDays * 20)}
        plan={selectedPlan}
        days={selectedDays}
        guestEmail={(guestEmail || '').trim().toLowerCase()}
        guestName={(guestName || '').trim()}
        onError={(msg) => {
          // Keep the modal open so the user sees the inline banner and
          // can immediately try a different payment method. Also mirror
          // to the page-level error so the message is still visible if
          // they close the modal manually.
          setError(msg);
        }}
        onStripeCheckout={() => handleSubscribe(selectedPlan)}
      />
    </div>
  );
}
